import { globalObservability } from '@opendoor/observability/slim';

/**
 * Variation index per arm, control first.
 *
 * GrowthBook assigns indices by declaration order, and the warehouse's
 * assignment query joins on the index rather than the label — so this pairing is
 * the experiment's contract, not a local naming choice. It holds across
 * production: over 60 days, index 0 carries `control` in all 15 experiments that
 * populate it (211,515 units), and index 1 is the second arm whatever it is
 * labelled.
 *
 * Derive exposures from this via {@link armExposure} rather than writing an index
 * at a call site: a variant and an index that disagree move a population between
 * arms, and nothing downstream can detect it.
 */
const VARIATION_IDS = { control: 0, treatment: 1 } as const;

type ExperimentVariant = keyof typeof VARIATION_IDS;

/**
 * The arm a surface was bucketed into, for the one exposure event a holdout
 * needs.
 *
 * Both arms report. A holdout measures treatment against the sellers who were
 * eligible and got nothing, so an exposure fired only when the treatment renders
 * would leave the control arm with no denominator to compare against.
 */
export interface ExperimentExposure {
  /** Matches the experiment's tracking key, which is the flag key. */
  experimentName: string;
  variant: ExperimentVariant;
  /**
   * Index of the variation, derived from `variant` via {@link VARIATION_IDS}.
   *
   * The warehouse's assignment query drops rows where this is null, so an
   * exposure without it is not a degraded row — it is no row, and the arm it
   * belonged to silently loses its population.
   */
  variationId: number;
}

type ArmExposureOptions = {
  /** The experiment's tracking key, which is the flag key. */
  experimentName: string;
  /** Which arm rendered. Its variation index is derived, never supplied. */
  variant: ExperimentVariant;
};

/** Builds the exposure for an arm, so the index is never written by hand. */
export function armExposure({ experimentName, variant }: ArmExposureOptions): ExperimentExposure {
  return { experimentName, variant, variationId: VARIATION_IDS[variant] };
}

/**
 * Reports that this seller saw the arm they were bucketed into.
 *
 * Fired from the browser rather than by the service that resolved the flag: the
 * Go GrowthBook SDK has no tracking callback wired, so a flag read on its own
 * produces no assignment row anywhere. Firing at render also measures the right
 * thing — a seller who resolved a flag and never saw the form is not exposed to
 * anything.
 */
export function reportExperimentExposure({
  experimentName,
  variant,
  variationId,
}: ExperimentExposure): void {
  try {
    globalObservability.trackJourneyEvent('experimentation', 'impression', {
      experimentName,
      variant,
      variationId,
    });
  } catch (error) {
    reportExposureTrackingError(experimentName, error);
  }
}

/**
 * `trackJourneyEvent` fans out synchronously to Journey, GTM, Datadog and Sentry
 * and guards none of it, so a blocked or half-initialised sink throws into
 * whatever called it. This one is called from an effect on both arms of the
 * account form, and an effect that throws with no boundary beneath it unmounts
 * the tree — so an ad blocker would take the login form down for every gated
 * seller, not just the treated half.
 *
 * Losing an exposure costs one row. Losing the form costs the login.
 */
function reportExposureTrackingError(experimentName: string, error: unknown): void {
  try {
    globalObservability.trackError(
      `${experimentName}-exposure-journey-event-failed`,
      error instanceof Error ? error : new Error(String(error)),
    );
  } catch {
    // The sink being unavailable is the case this exists for.
  }
}
